import { isB2B } from '@/core/settings/utils';
import { merchandisingLabels } from '@/core/tamagoshi/MerchandisingTag/interfaces';
import type { Merchandising } from '@/core/tamagoshi/ProductCard/interfaces';
import type { ProductCardProps } from '@/core/tamagoshi/ProductCard/ProductCard';
import type { Product } from '@/domains/productDiscovery/algolia/interfaces/product';
import { productHasDiscount } from '@/productDiscovery/CommercialAnimation/utils/getProductPromoTags';

interface MerchandisingValueParams {
  bestQualityOfferEnabled?: boolean;
  categoryId?: number;
  couponLabelDisabled?: boolean;
  isSponsored?: boolean;
  modelId?: number;
  product: Product;
  rankingAbtest?: boolean;
  bulkLabelEnabled?: boolean;
  proPriceAdvantageLabelEnabled?: boolean;
}

const getB2CMerchandisingValue = ({
  categoryId,
  isSponsored = false,
  modelId,
  product,
  rankingAbtest = false,
  couponLabelDisabled = false,
  bulkLabelEnabled = false,
}: MerchandisingValueParams): ProductCardProps['merchandising'] => {
  let merchandizing: Merchandising[] = [];
  const hasPromoted = modelId === product.modelId;

  if (isSponsored) {
    merchandizing.push('sponsored');
  }

  if (product.bulk && bulkLabelEnabled) {
    merchandizing.push('bulk');
  }

  if (
    !couponLabelDisabled &&
    product.coupon &&
    !merchandizing.includes('bulk')
  ) {
    merchandizing.push(merchandisingLabels.COUPON);
  }

  if (hasPromoted || merchandizing.length < 2) {
    const bannerKey = rankingAbtest ? 'bannerAlternate' : 'banner';
    if (
      product[bannerKey]?.categories.includes(`topSales.${categoryId}`) &&
      !isSponsored
    ) {
      merchandizing.push('topSale');
    }
  }

  const hasDiscount = productHasDiscount(
    product.prices.main,
    product.prices.retail,
  );

  if (
    product.commercialAnimation?.tags &&
    product.commercialAnimation?.tags.length > 0 &&
    (hasPromoted || merchandizing.length < 2) &&
    hasDiscount
  ) {
    if (product.commercialAnimation?.tags.includes(merchandisingLabels.PROMO)) {
      merchandizing.push(merchandisingLabels.PROMO);
    }

    if (
      product.commercialAnimation?.tags.includes(
        merchandisingLabels.BLACKFRIDAY,
      )
    ) {
      merchandizing.push(merchandisingLabels.BLACKFRIDAY);
    }

    if (product.commercialAnimation?.tags.includes(merchandisingLabels.SALES)) {
      merchandizing.push(merchandisingLabels.SALES);
    }

    if (
      product.commercialAnimation?.tags.includes(
        merchandisingLabels.MANOMANODAYS,
      )
    ) {
      merchandizing.push(merchandisingLabels.MANOMANODAYS);
    }

    if (
      product.commercialAnimation?.tags.includes(
        merchandisingLabels.LOWERED_PRICE,
      )
    ) {
      merchandizing.push(merchandisingLabels.LOWERED_PRICE);
    }
  }

  if (hasPromoted) {
    merchandizing.push('promoted');
  }

  if (
    product.isBestQualityOffer &&
    !merchandizing.includes('topSale') &&
    !merchandizing.includes('sponsored') &&
    !hasPromoted
  ) {
    merchandizing = ['bestQualityOffer'];
  }

  if (
    !couponLabelDisabled &&
    product.coupon &&
    !merchandizing.includes('sponsored') &&
    !hasPromoted &&
    !merchandizing.includes('bulk')
  ) {
    merchandizing = ['coupon'];
  }

  return merchandizing as ProductCardProps['merchandising'];
};

const getB2BMerchandisingValue = ({
  isSponsored = false,
  modelId,
  product,
  couponLabelDisabled = false,
  bulkLabelEnabled = false,
  proPriceAdvantageLabelEnabled = false,
}: MerchandisingValueParams): ProductCardProps['merchandising'] => {
  const merchandizing: Merchandising[] = [];
  const hasPromoted = modelId === product.modelId;

  if (isSponsored) {
    merchandizing.push('sponsored');
  }

  if (hasPromoted) {
    merchandizing.push('promoted');
  }

  if (product.bulk && bulkLabelEnabled) {
    merchandizing.push('bulk');
  }

  if (
    !couponLabelDisabled &&
    product.coupon &&
    !merchandizing.includes('sponsored') &&
    !merchandizing.includes('bulk') &&
    !hasPromoted &&
    merchandizing.length < 2
  ) {
    merchandizing.push('coupon');
  }

  if (
    product.commercialAnimation?.tags &&
    product.commercialAnimation?.tags.length > 0 &&
    product.commercialAnimation?.tags.includes(merchandisingLabels.PROMO) &&
    (hasPromoted || merchandizing.length < 2)
  ) {
    merchandizing.push(merchandisingLabels.PROMO);

    if (
      product.commercialAnimation?.tags.includes(
        merchandisingLabels.BLACKFRIDAY,
      )
    ) {
      merchandizing.push(merchandisingLabels.BLACKFRIDAY);
    }

    if (product.commercialAnimation?.tags.includes(merchandisingLabels.SALES)) {
      merchandizing.push(merchandisingLabels.SALES);
    }

    if (
      product.commercialAnimation?.tags.includes(
        merchandisingLabels.MANOMANODAYS,
      )
    ) {
      merchandizing.push(merchandisingLabels.MANOMANODAYS);
    }
  }
  if (
    product.commercialAnimation?.tags.includes(
      merchandisingLabels.LOWERED_PRICE,
    )
  ) {
    merchandizing.push(merchandisingLabels.LOWERED_PRICE);
  }

  if (
    proPriceAdvantageLabelEnabled &&
    product.proAdvantagePercentage &&
    product.proAdvantagePercentage > 0 &&
    merchandizing.length === 0
  ) {
    merchandizing.push(merchandisingLabels.PRO_PRICE_ADVANTAGE);
  }

  return merchandizing as ProductCardProps['merchandising'];
};

export const getMerchandisingValue = (
  merchandisingValueParams: MerchandisingValueParams,
): ProductCardProps['merchandising'] =>
  isB2B()
    ? getB2BMerchandisingValue(merchandisingValueParams)
    : getB2CMerchandisingValue(merchandisingValueParams);

export const getRetailPriceTimeLeftLabel = (
  product: Product,
  merchandisingValue?: Merchandising[],
): string | undefined =>
  merchandisingValue?.includes('promo')
    ? (product.commercialAnimation?.retailPriceTimeLeftLabel ?? undefined)
    : undefined;
